import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from '../components/Icon.tsx';
import { usersApi, type OrgUserRow, type OrgUserPatch } from '../features/users/api.ts';
import { AppRoleChips, AppRoleEditor } from '../features/appRoles/AppRoles.tsx';
import { invitesApi, type InviteRow } from '../features/invites/api.ts';
import {
  AppAccessFields,
  ProvisioningChips,
  emptyAppAccess,
  appAccessError,
  toAppProvisioning,
  type AppAccessState,
} from '../features/invites/AppAccessFields.tsx';
import { useAuthStore } from '../store/auth.ts';
import type { AppRolesResponse } from '../features/appRoles/api.ts';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return '?';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? first;
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}

function RoleBadge({ user }: { user: OrgUserRow }): JSX.Element {
  const label = user.role === 'SYS_ADMIN' ? 'System admin' : user.isOrgAdmin ? 'Admin' : 'Member';
  const cls = user.role === 'SYS_ADMIN' || user.isOrgAdmin ? 'pill-active' : 'pill-neutral';
  return <span className={`pill ${cls}`}>{label}</span>;
}

// A labelled group inside the Manage panel.
function ManageGroup({
  iconName,
  title,
  children,
}: {
  iconName: string;
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2">
        <Icon name={iconName} size={18} className="text-muted-foreground" />
        <span className="text-sm font-medium text-foreground/80">{title}</span>
      </div>
      {children}
    </div>
  );
}

// Identity editor — Dhivehi name + designations. Always-visible inputs inside
// the Manage panel (no nested open/close), with an explicit Save.
function IdentityForm({
  user,
  onSave,
  busy,
}: {
  user: OrgUserRow;
  onSave: (patch: OrgUserPatch) => void;
  busy: boolean;
}): JSX.Element {
  const [nameDv, setNameDv] = useState(user.nameDv ?? '');
  const [dv, setDv] = useState(user.designationDv ?? '');
  const [en, setEn] = useState(user.designationEn ?? '');
  const [section, setSection] = useState(user.section ?? '');
  const [saved, setSaved] = useState(false);

  const dirty =
    nameDv !== (user.nameDv ?? '') ||
    dv !== (user.designationDv ?? '') ||
    en !== (user.designationEn ?? '') ||
    section !== (user.section ?? '');

  const inputCls = 'input py-2';

  return (
    <div className="space-y-2.5">
      <div className="grid gap-2.5 sm:grid-cols-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Name (Dhivehi)</label>
          <input
            dir="rtl"
            value={nameDv}
            onChange={(e) => {
              setNameDv(e.target.value);
              setSaved(false);
            }}
            placeholder="ނަން"
            aria-label="Name (Dhivehi)"
            className={`${inputCls} font-dhivehi text-base text-right`}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Designation (Dhivehi)</label>
          <input
            dir="rtl"
            value={dv}
            onChange={(e) => {
              setDv(e.target.value);
              setSaved(false);
            }}
            placeholder="މަޤާމު"
            aria-label="Designation (Dhivehi)"
            className={`${inputCls} font-dhivehi text-base text-right`}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Designation (English)</label>
          <input
            value={en}
            onChange={(e) => {
              setEn(e.target.value);
              setSaved(false);
            }}
            placeholder="e.g. Council Member"
            aria-label="Designation (English)"
            className={inputCls}
          />
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">Section</label>
        <input
          value={section}
          onChange={(e) => {
            setSection(e.target.value);
            setSaved(false);
          }}
          placeholder="e.g. Finance Section"
          aria-label="Section"
          className={inputCls}
        />
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={busy || !dirty}
          onClick={() => {
            onSave({ nameDv: nameDv || null, designationDv: dv || null, designationEn: en || null, section: section || null });
            setSaved(true);
          }}
          className="btn-secondary btn-sm"
        >
          <Icon name="check" size={16} /> {busy ? 'Saving…' : 'Save identity'}
        </button>
        {saved && !dirty && (
          <span className="text-xs text-[hsl(var(--success))]">Saved.</span>
        )}
      </div>
    </div>
  );
}

function MemberCard({
  u,
  isSelf,
  isOrgAdmin,
  appRoles,
  hasWapsign,
  hasWapmeet,
  hasWapstat,
  hasWappro,
  hasWapdocs,
  busy,
  resetDone,
  resetPending,
  onUpdate,
  onResetPassword,
  onAppRolesDone,
}: {
  u: OrgUserRow;
  isSelf: boolean;
  isOrgAdmin: boolean;
  appRoles?: AppRolesResponse;
  hasWapsign: boolean;
  hasWapmeet: boolean;
  hasWapstat: boolean;
  hasWappro: boolean;
  hasWapdocs: boolean;
  busy: boolean;
  resetDone: boolean;
  resetPending: boolean;
  onUpdate: (patch: OrgUserPatch) => void;
  onResetPassword: () => void;
  onAppRolesDone: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const isSysAdmin = u.role === 'SYS_ADMIN';

  return (
    <li className="card-paper overflow-hidden">
      {/* Summary row — scannable at a glance */}
      <div className="flex items-start gap-3 p-4">
        <div className="grid size-10 shrink-0 place-items-center rounded-full bg-[hsl(var(--tertiary-container))] text-[hsl(var(--on-tertiary-container))] text-sm font-medium">
          {initials(u.name)}
        </div>

        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-medium text-foreground">{u.name}</span>
            {u.nameDv && (
              <span dir="rtl" className="font-dhivehi text-base text-foreground">
                {u.nameDv}
              </span>
            )}
            <RoleBadge user={u} />
            <span className={`pill ${u.isActive ? 'pill-success' : 'pill-neutral'}`}>
              {u.isActive ? 'Active' : 'Disabled'}
            </span>
          </div>

          <div className="truncate text-xs text-muted-foreground">{u.email}</div>

          {(u.designationEn || u.designationDv) && (
            <div className="text-xs text-muted-foreground">
              {u.designationEn}
              {u.designationEn && u.designationDv ? ' · ' : ''}
              {u.designationDv && (
                <span dir="rtl" className="font-dhivehi">
                  {u.designationDv}
                </span>
              )}
            </div>
          )}

          {isOrgAdmin && <AppRoleChips email={u.email} roles={appRoles} />}
        </div>

        {/* Manage toggle — single entry point to all heavy controls */}
        {isSysAdmin ? (
          <span className="shrink-0 self-center text-xs text-muted-foreground">—</span>
        ) : (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
              open
                ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]'
                : 'bg-[hsl(var(--surface-container-high))] text-foreground hover:bg-[hsl(var(--surface-container-highest))]'
            }`}
          >
            <Icon name="tune" size={16} /> Manage
            <Icon
              name="expand_more"
              size={16}
              className={`transition-transform ${open ? 'rotate-180' : ''}`}
            />
          </button>
        )}
      </div>

      {/* Manage panel — progressive disclosure, grouped sections */}
      {!isSysAdmin && open && (
        <div className="border-t border-border/50 bg-[hsl(var(--surface-container-low))] px-4 py-4 space-y-5 animate-in fade-in slide-in-from-top-1 duration-150">
          <ManageGroup iconName="badge" title="Identity">
            <IdentityForm user={u} busy={busy} onSave={onUpdate} />
          </ManageGroup>

          {isOrgAdmin && (
            <ManageGroup iconName="apps" title="App access">
              <AppRoleEditor
                user={u}
                roles={appRoles}
                hasWapsign={hasWapsign}
                hasWapmeet={hasWapmeet}
                hasWapstat={hasWapstat}
                hasWappro={hasWappro}
                hasWapdocs={hasWapdocs}
                updateAppRoles={(userId, body) => usersApi.updateAppRoles(userId, body)}
                provisionApp={(userId, body) => usersApi.provisionApp(userId, body)}
                onDone={onAppRolesDone}
              />
            </ManageGroup>
          )}

          <ManageGroup iconName="manage_accounts" title="Account">
            <div className="flex flex-wrap items-center gap-2">
              {u.isOrgAdmin ? (
                <button
                  type="button"
                  disabled={busy || isSelf}
                  onClick={() => {
                    if (confirm(`Remove admin rights from ${u.name}?`))
                      onUpdate({ isOrgAdmin: false });
                  }}
                  className="btn-secondary btn-sm"
                >
                  <Icon name="manage_accounts" size={16} /> Remove admin
                </button>
              ) : (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    if (confirm(`Make ${u.name} an admin?`)) onUpdate({ isOrgAdmin: true });
                  }}
                  className="btn-secondary btn-sm"
                >
                  <Icon name="manage_accounts" size={16} /> Make admin
                </button>
              )}

              <button
                type="button"
                disabled={busy || isSelf}
                onClick={() => onUpdate({ isActive: !u.isActive })}
                className="btn-secondary btn-sm"
              >
                <Icon name="power_settings_new" size={16} /> {u.isActive ? 'Deactivate' : 'Activate'}
              </button>

              <button
                type="button"
                disabled={resetPending}
                onClick={() => {
                  if (confirm(`Send a password reset email to ${u.email}?`)) onResetPassword();
                }}
                className="btn-secondary btn-sm"
              >
                <Icon name="key" size={16} /> Reset password
              </button>

              {resetDone && (
                <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--success))]">
                  <Icon name="mark_email_read" size={16} /> Email sent
                </span>
              )}
            </div>
            {isSelf && (
              <p className="text-xs text-muted-foreground">
                You can&rsquo;t change your own admin rights or active status.
              </p>
            )}
          </ManageGroup>
        </div>
      )}
    </li>
  );
}

export function Team(): JSX.Element {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const isOrgAdmin = me?.role === 'ORG_ADMIN';
  const [resetDone, setResetDone] = useState<Record<string, boolean>>({});
  const [inviteOpen, setInviteOpen] = useState(false);

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['team', 'users'],
    queryFn: () => usersApi.list().then((r) => r.users),
  });
  const { data: invites } = useQuery({
    queryKey: ['team', 'invites'],
    queryFn: () => invitesApi.list(),
  });
  // Per-app roles are an org-admin-only capability.
  const { data: appRoles } = useQuery({
    queryKey: ['team', 'app-roles'],
    queryFn: () => usersApi.appRoles(),
    enabled: isOrgAdmin,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['team'] });
  };
  const invalidateAppRoles = () => {
    void qc.invalidateQueries({ queryKey: ['team', 'app-roles'] });
  };

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: OrgUserPatch }) => usersApi.update(id, patch),
    onSuccess: invalidate,
  });

  const resetPassword = useMutation({
    mutationFn: (id: string) => usersApi.resetPassword(id),
    onSuccess: (_d, id) => setResetDone((s) => ({ ...s, [id]: true })),
  });

  const [email, setEmail] = useState('');
  const [asAdmin, setAsAdmin] = useState(false);
  const [appAccess, setAppAccess] = useState<AppAccessState>(emptyAppAccess);
  const [inviteResult, setInviteResult] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: () => invitesApi.create(email, asAdmin, toAppProvisioning(appAccess)),
    onSuccess: (r) => {
      setInviteResult(r.invite.email);
      setInviteError(null);
      setEmail('');
      setAsAdmin(false);
      setAppAccess(emptyAppAccess);
      invalidate();
    },
    onError: () => setInviteError('Failed to send invite. Check the email address.'),
  });

  const memberCount = users?.length ?? 0;
  const pendingCount = invites?.length ?? 0;

  return (
    <div className="page space-y-8">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm text-muted-foreground">Organization</p>
          <h1 className="text-[2rem] md:text-[2.5rem] font-normal leading-tight mt-1 text-ink">
            Team
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Manage members, app access and invitations for your organization.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setInviteResult(null);
            setInviteError(null);
            setInviteOpen(true);
          }}
          className="btn-primary self-start sm:self-auto"
        >
          <Icon name="person_add" size={18} /> Invite member
        </button>
      </header>

      {/* Members */}
      <section className="space-y-3">
        <div className="section-heading">
          <h2 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Icon name="group" size={18} /> Members
            {memberCount > 0 && (
              <span className="rounded-full bg-[hsl(var(--surface-container-highest))] px-2 py-0.5 text-[11px] text-muted-foreground tabular">
                {memberCount}
              </span>
            )}
          </h2>
        </div>

        {(update.isError || resetPassword.isError) && (
          <p className="rounded-xl bg-[hsl(var(--error-container))] px-4 py-3 text-sm text-[hsl(var(--on-error-container))]">
            Something went wrong — the change was not applied.
          </p>
        )}

        {usersLoading ? (
          <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
            Loading members…
          </p>
        ) : memberCount === 0 ? (
          <div className="card-paper px-4 py-10 text-center">
            <Icon name="group" size={28} className="mx-auto block text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">No members yet.</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {users?.map((u) => (
              <MemberCard
                key={u.id}
                u={u}
                isSelf={u.id === me?.id}
                isOrgAdmin={isOrgAdmin}
                appRoles={appRoles}
                hasWapsign={!!me?.hasWapsign}
                hasWapmeet={!!me?.hasWapmeet}
                hasWapstat={!!me?.hasWapstat}
                hasWappro={!!me?.hasWappro}
                hasWapdocs={!!me?.hasWapdocs}
                busy={update.isPending && update.variables?.id === u.id}
                resetDone={!!resetDone[u.id]}
                resetPending={resetPassword.isPending}
                onUpdate={(patch) => update.mutate({ id: u.id, patch })}
                onResetPassword={() => resetPassword.mutate(u.id)}
                onAppRolesDone={invalidateAppRoles}
              />
            ))}
          </ul>
        )}
      </section>

      {/* Pending invites */}
      {pendingCount > 0 && (
        <section className="space-y-3">
          <div className="section-heading">
            <h2 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Icon name="mark_email_read" size={18} /> Pending invites
              <span className="rounded-full bg-[hsl(var(--surface-container-highest))] px-2 py-0.5 text-[11px] text-muted-foreground tabular">
                {pendingCount}
              </span>
            </h2>
          </div>
          <ul className="space-y-2">
            {invites?.map((inv: InviteRow) => (
              <li
                key={inv.id}
                className="card-paper flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <Icon name="verified_user" size={18} className="shrink-0 text-muted-foreground" />
                  <span className="truncate text-sm text-foreground">{inv.email}</span>
                  <span className="pill pill-neutral">{inv.isOrgAdmin ? 'Admin' : 'Member'}</span>
                  <ProvisioningChips prov={inv.appProvisioning} />
                </div>
                <span className="mono-meta shrink-0 text-muted-foreground">
                  expires {new Date(inv.expiresAt).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Invite slide-over */}
      {inviteOpen && (
        <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label="Invite member">
          <button
            type="button"
            aria-label="Close"
            onClick={() => setInviteOpen(false)}
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-150"
          />
          <div className="relative ml-auto flex h-full w-full max-w-md flex-col overflow-y-auto bg-[hsl(var(--surface-container-low))] shadow-2xl animate-in slide-in-from-right duration-200 sm:rounded-l-[1.75rem]">
            <div className="flex items-center justify-between border-b border-border/50 px-5 py-4">
              <div className="flex items-center gap-2">
                <Icon name="person_add" size={20} className="text-primary" />
                <h2 className="text-xl font-medium text-ink leading-none">Invite a teammate</h2>
              </div>
              <button
                type="button"
                onClick={() => setInviteOpen(false)}
                aria-label="Close"
                className="grid size-10 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-black/[0.05] hover:text-foreground dark:hover:bg-white/[0.06]"
              >
                <Icon name="close" size={20} />
              </button>
            </div>

            <form
              className="flex-1 space-y-5 px-5 py-5"
              onSubmit={(e) => {
                e.preventDefault();
                if (email.trim()) invite.mutate();
              }}
            >
              <div className="space-y-1.5">
                <label htmlFor="invite-email" className="field-label">
                  Email address
                </label>
                <input
                  id="invite-email"
                  aria-label="Email"
                  type="email"
                  placeholder="teammate@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="input"
                />
              </div>

              <label className="flex items-center gap-2.5 rounded-xl bg-[hsl(var(--surface-container))] px-4 py-3 text-sm">
                <input
                  type="checkbox"
                  className="size-4 shrink-0 rounded accent-primary"
                  checked={asAdmin}
                  onChange={(e) => setAsAdmin(e.target.checked)}
                />
                <span>
                  <span className="text-foreground">Make this person an admin</span>
                  <span className="block text-xs text-muted-foreground">
                    Admins can manage members and invitations.
                  </span>
                </span>
              </label>

              <AppAccessFields
                value={appAccess}
                onChange={setAppAccess}
                hasWapsign={!!me?.hasWapsign}
                hasWapmeet={!!me?.hasWapmeet}
                hasWapstat={!!me?.hasWapstat}
                hasWappro={!!me?.hasWappro}
                hasWapdocs={!!me?.hasWapdocs}
              />

              {inviteError && <p className="text-sm text-destructive">{inviteError}</p>}
              {inviteResult && (
                <div className="flex items-start gap-3 rounded-xl bg-[hsl(var(--secondary-container))] p-4 text-[hsl(var(--on-secondary-container))]">
                  <Icon name="mark_email_read" size={20} className="mt-0.5 shrink-0" />
                  <p className="text-xs">
                    Invite emailed to <span className="font-medium">{inviteResult}</span>.
                  </p>
                </div>
              )}
            </form>

            <div className="flex items-center justify-end gap-2 border-t border-border/50 px-5 py-4">
              <button
                type="button"
                onClick={() => setInviteOpen(false)}
                className="btn-ghost"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  if (email.trim()) invite.mutate();
                }}
                disabled={invite.isPending || !email.trim() || !!appAccessError(appAccess)}
                className="btn-primary"
              >
                {invite.isPending ? 'Sending…' : 'Send invite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
